'use client'
import React from 'react'
import { installBrowserDomGuard } from '@/lib/browser-dom-guard'

installBrowserDomGuard()

import { AuthProvider } from '@/contexts/AuthContext'
import { PortfolioProviderWithQuery } from '@/contexts/PortfolioContext'
import { ScenarioProvider } from '@/contexts/ScenarioContext'
import { DashboardProvider } from '@/contexts/DashboardContext'
import { CurrencyProvider } from '@/contexts/CurrencyContext'
import { CookieConsentProvider } from '@/contexts/CookieConsentContext'
import { ValueMaskingProvider } from '@/contexts/ValueMaskingContext'
import { HelmetProvider } from 'react-helmet-async'
import LayoutClient from './LayoutClient'
import CookieBanner from '@/components/CookieBanner'
import { NotificationProvider } from '@/contexts/NotificationContext'
import { User, Portfolio, Scenario } from '@/types'
import { ReviewRequestTriggerProvider } from '@/hooks/useReviewRequestTrigger'
import { MoodProvider } from '@/contexts/MoodContext';

function AppProviders({ 
  children, 
  initialPortfolios, 
  initialActivePortfolio,
  initialScenarios,
  initialSelectedScenario
}: {
  children: React.ReactNode;
  initialPortfolios?: Portfolio[];
  initialActivePortfolio?: Portfolio | null;
  initialScenarios?: Scenario[];
  initialSelectedScenario?: string | null;
}) {
  // One provider tree for every route and auth state. This used to return a
  // slimmer tree (no Portfolio/Scenario/Dashboard/ReviewRequest providers)
  // while `isPublic && !user && authLoading`, to spare anonymous blog
  // pageviews the provider mount (DEL-1019/DEL-1012). It never achieved that:
  // AuthContext clears `loading` in its mount effect (synchronously on the
  // /api/auth/me-skippable routes, after /me otherwise), and the anonymous
  // visitor then got the full tree anyway. Swapping between two differently
  // shaped trees changed the element path above LayoutClient, so every public
  // page unmounted and remounted when auth resolved and ran its mount effects
  // twice - /verify-email POSTed the token twice per load (2026-10-17). The
  // providers already gate their queries on `user`, so keeping them mounted
  // for anonymous visitors costs nothing the old code didn't already pay.
  return (
    <NotificationProvider>
      <HelmetProvider>
        <CookieConsentProvider>
          <CurrencyProvider>
            <ValueMaskingProvider>
              <PortfolioProviderWithQuery
                initialPortfolios={initialPortfolios}
                initialActivePortfolio={initialActivePortfolio}
              >
                <ScenarioProvider
                  initialScenarios={initialScenarios}
                  initialSelectedScenario={initialSelectedScenario}
                >
                  <DashboardProvider>
                    <MoodProvider>
                      <ReviewRequestTriggerProvider>
                        <CookieBanner />
                        <LayoutClient>{children}</LayoutClient>
                      </ReviewRequestTriggerProvider>
                    </MoodProvider>
                  </DashboardProvider>
                </ScenarioProvider>
              </PortfolioProviderWithQuery>
            </ValueMaskingProvider>
          </CurrencyProvider>
        </CookieConsentProvider>
      </HelmetProvider>
    </NotificationProvider>
  )
}

export default function SSRLayoutWrapperClient({
  children,
  initialPortfolios = [],
  initialActivePortfolio = null,
  initialUser = null,
  initialScenarios = [],
  initialSelectedScenario = null
}: {
  children: React.ReactNode;
  initialPortfolios?: Portfolio[];
  initialActivePortfolio?: Portfolio | null;
  initialUser?: User | null;
  initialScenarios?: Scenario[];
  initialSelectedScenario?: string | null;
}) {
  return (
    <AuthProvider initialUser={initialUser}>
      <AppProviders 
        initialPortfolios={initialPortfolios}
        initialActivePortfolio={initialActivePortfolio}
        initialScenarios={initialScenarios}
        initialSelectedScenario={initialSelectedScenario}
      >
        {children}
      </AppProviders>
    </AuthProvider>
  )
}
